import Information16 from '@carbon/icons-react/es/information/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Dictionary } from 'lodash';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Account } from 'api/account';
import { RoleRate } from 'api/rate-cards';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { InputNumber } from 'components/atoms/input';
import { Table } from 'components/molecules/table';
import { SingleLineEllipsis } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { UpdateRateForCurrency } from '../types';

const RoleNameWrapper = styled.div`
  display: flex;
  align-items: center;
  height: var(--layout-04);
  padding-left: var(--spacing-05);
  gap: var(--spacing-03);
`;

const RoleName = styled.div`
  max-width: 320px;
  ${SingleLineEllipsis};
`;

const RateInput = styled(InputNumber)`
  background-color: transparent;
  border: none;
  height: var(--layout-04);

  input {
    height: var(--layout-04) !important;
  }

  &.ant-input-number-readonly,
  &.ant-input-number-readonly.ant-input-number-focused {
    outline: none;
  }
`;

const RedTrashCan = styled(TrashCan16)`
  color: var(--red-60);
`;

const DeleteButtonWrapper = styled(Button)`
  border: none;
`;

const StyledTable = styled(Table)`
  position: relative;
  margin: var(--spacing-05) 0;

  .ant-table-cell {
    background-color: transparent;
    border-bottom: none;
    padding: 0;
  }

  .ant-table-row {
    &:hover {
      > td {
        background-color: var(--gray-10) !important;
      }
    }

    ${DeleteButtonWrapper} {
      visibility: hidden;
    }

    &:hover ${DeleteButtonWrapper} {
      visibility: visible;
    }
  }

  &.disabled::after {
    content: '';
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    background-color: var(--white-100);
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

interface Props {
  account: Account;
  roleRates: Dictionary<RoleRate>;
  isInputDisabled: boolean;
  updateRoleRate: (props: UpdateRateForCurrency) => void;
  deleteRow: (roleId: number) => void;
}

export const RatesTable: FC<Props> = ({
  account,
  roleRates,
  isInputDisabled,
  updateRoleRate,
  deleteRow,
}) => {
  const { currency } = account;

  const roleTitle = useTranslation(
    'settings.rate_cards.drawer.table.header.role',
  );
  const rateTitle = useTranslation(
    'settings.rate_cards.drawer.table.header.rates',
    { currencySymbol: `${currency.currencySymbol}` },
  );
  const deleteRowTooltip = useTranslation(
    'settings.rate_cards.drawer.table.row.delete',
  );

  const columns: ColumnsType<RoleRate> = [
    {
      title: roleTitle,
      dataIndex: 'roleName',
      key: 'roleName',
      width: '360px',
      render: (roleName, record) => {
        return (
          <RoleNameWrapper>
            <Avatar name={roleName} />
            <RoleName>{roleName}</RoleName>
            {record.roleId === -1 && (
              <Tooltip
                title={
                  <FormattedMessage id="settings.rate_cards.drawer.table.row.default.tooltip" />
                }
              >
                <Information16 />
              </Tooltip>
            )}
          </RoleNameWrapper>
        );
      },
      // sortDirections: ['ascend', 'descend', 'ascend'],
      // defaultSortOrder: 'ascend',
      // sorter: ({ roleName: a }, { roleName: b }) => sortByText(a, b),
    },
    // To be used with multi-currencies
    // ...currencies.map(({ currencyCode, currencySymbol }) => ({
    //   title: `${currencyCode} (${currencySymbol})`,
    //   dataIndex: 'rateCurrencyMap',
    //   key: `${currencyCode}-rate`,
    //   render: (rateCurrencyMap, record) => {
    //     console.log('### rateCurrencyMap', rateCurrencyMap);
    //     console.log('### record', record);

    //     return (
    //       <RateInput
    //         // eslint-disable-next-line react/destructuring-assignment
    //         value={rateCurrencyMap[currencyCode]?.rate ?? 0}
    //         min={0}
    //         formatter={(value) =>
    //           `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    //         }
    //         onChange={(val) => {
    //           updateRoleRate({
    //             roleId: record.roleId,
    //             roleName: record.roleName,
    //             currencyCode,
    //             rate: (val as number) ?? 0,
    //           });
    //         }}
    //         onPressEnter={({ currentTarget }) => currentTarget.blur()}
    //       />
    //     );
    //   },
    // })),
    {
      title: rateTitle,
      dataIndex: 'multiCurrencyRates',
      key: 'rate',
      render: (multiCurrencyRates, record) => {
        return (
          <RateInput
            value={
              // eslint-disable-next-line react/destructuring-assignment
              multiCurrencyRates[currency.currencyCode]?.billRate ?? 0
            }
            min={0}
            formatter={(value) =>
              `${currency.currencySymbol} ${value}`.replace(
                /\B(?=(\d{3})+(?!\d))/g,
                ',',
              )
            }
            readOnly={isInputDisabled}
            onChange={(val) => {
              updateRoleRate({
                roleId: record.roleId,
                roleName: record.roleName,
                currency,
                billRate: (val as number) ?? 0,
              });
            }}
            onPressEnter={({ currentTarget }) => currentTarget.blur()}
          />
        );
      },
    },
    {
      title: '',
      dataIndex: 'roleId',
      key: 'delete',
      width: '48px',
      render: (roleId) => {
        return (
          roleId !== -1 &&
          !isInputDisabled && (
            <Tooltip title={deleteRowTooltip} placement="topRight">
              <DeleteButtonWrapper
                type="ghost"
                icon={<RedTrashCan />}
                onClick={() => deleteRow(roleId)}
              />
            </Tooltip>
          )
        );
      },
    },
  ];

  const dataSource = useMemo(() => {
    const { [-1]: defaultRate, ...rest } = roleRates;

    return [defaultRate, ...Object.values(rest)];
  }, [roleRates]);

  return (
    <StyledTable
      className={isInputDisabled ? 'disabled' : ''}
      columns={columns}
      dataSource={dataSource}
      pagination={false}
    />
  );
};
